import React from 'react';
import PropTypes from 'prop-types';
import cx from 'classnames';

import Text from '../Typography';
import { clamp } from '../utils';

import styles from './Pager.less';

const firstPage = 1;

/**
 * **Access:**
 * ```js
 * import { Pagination } from '@iss/client-react-components'
 *
 * ...
 * render () {
 * 	return <Pagination.Pager .../>
 * }
 * ```
 * A common pager control. Supports next/previous.
 */
export default class Pager extends React.PureComponent {
  static propTypes = {
    /** The number of items per page */
    pageSize: PropTypes.number.isRequired,
    /** The total number of items */
    totalCount: PropTypes.number.isRequired,
    /** The current page number (not the index, this is 1 based) */
    currentPage: PropTypes.number.isRequired,
    /** Hide the next/previous arrow links */
    omitLinks: PropTypes.bool,
    /** A function to produce a route or link target (href) for any given page. */
    pageLinkFactory: PropTypes.func,
    /** A callback to handle page transitions (if not routing) */
    onPageChange: PropTypes.func,
  };

  static defaultProps = {
    pageLinkFactory: () => '#',
  };

  onPageChange(pageId, event) {
    const { onPageChange } = this.props;
    if (onPageChange) {
      event.preventDefault();
      onPageChange(pageId, event);
    }
  }

  get total() {
    return clamp(Math.max(0, this.props.totalCount));
  }

  get end() {
    const { currentPage: page, pageSize } = this.props;
    return clamp(Math.min(Math.max(1, page) * pageSize, this.total));
  }

  get start() {
    const { currentPage: page, pageSize: size, totalCount: total } = this.props;
    return clamp(Math.min(total, 1 + (page - 1) * size));
  }

  get pageCount() {
    return clamp(Math.ceil(this.total / this.props.pageSize));
  }

  render() {
    const {
      end,
      start,
      total,
      pageCount: lastPage,

      props: { className, currentPage: page },
    } = this;

    return lastPage < 1 ? null : (
      <div className={cx(styles.pager, className)}>
        <Text face="subtext1">
          {start} - {end} of {total}
        </Text>

        {this.renderLink(page - 1, page <= firstPage, styles.previous, 'Previous Page')}
        {this.renderLink(page + 1, page >= lastPage, styles.next, 'Next Page')}
      </div>
    );
  }

  renderLink(toPage, disabled, cls, label) {
    const { currentPage, omitLinks, pageLinkFactory } = this.props;
    const href = disabled ? null : pageLinkFactory(toPage, currentPage);
    const isLink = href && href !== '' && href !== '#';
    //jsx-a11y/anchor-is-valid complains when we render a disabled link... this shuts it up.
    const Tag = disabled || !isLink ? 'button' : 'a';
    return omitLinks ? null : (
      <Tag
        onClick={disabled ? null : (e) => this.onPageChange(toPage, e)}
        href={isLink ? href : void 0}
        disabled={disabled}
        className={cls}
        title={label}
      >
        {label}
      </Tag>
    );
  }
}
